import React, { useState, useEffect } from 'react';

import lodash from 'lodash';
import moment from 'moment';

import { ThemeProvider } from '@material-ui/styles';

import DefaultMaterialTable from 'common/util/materialTable';
import MaterialTable from '@material-table/core';
import { optionsBuilder } from 'common/util/materialTable/options';
import { theme } from 'common/util/materialTable/theme';
import { themeStudy } from 'common/util/materialTable/themeStudy';
import { Conditional } from 'common/components/layout';
import { Button, BUTTON } from 'components/common/Button/Button';

import DelegationPopoverTask from 'pages/delegationOfAuthority/v2/components/DelegationPopoverTask/DelegationPopoverTask';
import SignatureStatus from './SignatureStatus';

import styles from './DelegationTable.scss';
import { localization, tableIcons } from '../../../../common/util/materialTable';
import icons from 'styles/icons/icons.scss';
import Tooltip from '@material-ui/core/Tooltip';
import { withStyles } from '@material-ui/core/styles';
import { datesSort } from '../../../../common/util/materialTable/sorting';

const getValidSignaturesByRole = (arrSig, role) => {
    return arrSig.filter(
        (expectation) =>
            (expectation.signatureStatus === 'PENDING' ||
                expectation.signatureStatus === 'COMPLETED') &&
            expectation.signatureType.includes(role)
    );
};

const ConflictRoleTooltip = withStyles({
    tooltip: {
        opacity: '0.85',
        color: 'white',
        backgroundColor: '#28282E',
        padding: '5px',
        fontSize: 14,
    }
})(Tooltip);

function DelegationTable(props) {
    const [rows, setRows] = useState([]);
    const [columns, setColumns] = useState([]);

    useEffect(() => {
        const { delegations, signatureExpectationsAll } = props;
        setRows(gatherDelegationData(delegations, signatureExpectationsAll));
        setColumns(buildDelegationColumns());

        return () => {
            setRows([]);
            setColumns([]);
        };
    }, [props.delegations]);

    const gatherDelegationData = (delegations, signatureExpectationsAll) => {
        const sortedByName = lodash.sortBy(delegations, (doa) =>
            lodash.toLower(lodash.get(doa, ['user', 'displayName'], ''))
        );
        return lodash.map(sortedByName, (doa) => {
            const signatureExpectations = signatureExpectationsAll.filter(expectation => expectation.delegationId === doa.id);

            return {
                delegationName: lodash.get(doa, ['user', 'displayName']),
                delegationRole: lodash.get(doa, ['role', 'name']),
                delegationRoleId: lodash.get(doa, ['role', 'id']),
                delegatedTasks: lodash.get(doa, ['tasks']),
                delegationSignatures: signatureExpectations,
                startDate: parseDateFormat(doa.startDate),
                endDate: parseDateFormat(doa.endDate),
                rawStartDate: doa.startDate,
                rawEndDate: doa.endDate,
                pendingPISignatures: getValidSignaturesByRole(signatureExpectations, 'PI_START'),
                pendingStaffSignatures: getValidSignaturesByRole(signatureExpectations, 'STAFF_START'),
                id: lodash.get(doa, ['id']),
                userId: lodash.get(doa, ['user', 'id']),
                pendingSignatures: doa.signatures
                    ? doa.signatures.filter((item) => item.signatureStatus === 'PENDING')
                    : [],
                completeSignatures: doa.signatures
                    ? doa.signatures.filter((item) => item.signatureStatus === 'COMPLETED')
                    : [],
            };
        });
    };

    const parseDateFormat = (date) => {
        if (lodash.isNil(date)) {
            return null;
        }
        return moment.utc(date).format('L');
    };

    const buildDelegationColumns = () => {
        const delegationColumns = [
            {
                title: 'Name',
                field: 'delegationName',
                hidden: false,
                headerStyle: {
                    width: '16.7%',
                    minWidth: '16.7%',
                },
                cellStyle: {
                    width: '16.7%',
                    minWidth: '16.7%',
                },
            },
            {
                title: 'Role',
                field: 'delegationRole',
                hidden: false,
                headerStyle: {
                    width: '17%',
                    minWidth: '17%',
                },
                cellStyle: {
                    width: '17%',
                    minWidth: '17%',
                },
            },
            {
                title: 'Tasks',
                hidden: false,
                headerStyle: {
                    width: '12%',
                    minWidth: '12%',
                },
                cellStyle: {
                    width: '12%',
                    minWidth: '12%',
                },
                render: (data) => {
                    const taskCount = lodash.get(data, 'delegatedTasks.length');
                    const tasks = lodash.get(data, 'delegatedTasks');

                    return (
                        <Conditional condition={taskCount > 0}>
                            <DelegationPopoverTask tasks={tasks} />
                        </Conditional>
                    );
                },
                customSort: (a, b) =>
                    lodash.get(a, 'delegatedTasks.length', 0) -
                    lodash.get(b, 'delegatedTasks.length', 0),
            },
            {
                title: 'Start Date',
                field: 'startDate',
                hidden: false,
                headerStyle: {
                    width: '12.5%',
                    minWidth: '12.5%',
                },
                cellStyle: {
                    width: '12.5%',
                    minWidth: '12.5%',
                },
                customSort: (a, b) => {
                    const aStartDate = lodash.get(a, 'rawStartDate', null);
                    const bStartDate = lodash.get(b, 'rawStartDate', null);
                    return datesSort(aStartDate, bStartDate);
                },
            },
            {
                title: 'End Date',
                field: 'endDate',
                hidden: false,
                headerStyle: {
                    width: '12.5%',
                    minWidth: '12.5%',
                },
                cellStyle: {
                    width: '12.5%',
                    minWidth: '12.5%',
                },
                customSort: (a, b) => {
                    const aEndDate = lodash.get(a, 'rawEndDate', null);
                    const bEndDate = lodash.get(b, 'rawEndDate', null);
                    return datesSort(aEndDate, bEndDate);
                },
            },
            {
                title: 'P.I. Signature',
                field: 'pendingPISignatures',
                hidden: false,
                headerStyle: {
                    width: '12.5%',
                    minWidth: '180px',
                },
                cellStyle: {
                    width: '12.5%',
                    minWidth: '180px',
                },
                render: (data) => {
                    const piSignatureExpectations = lodash.get(data, 'pendingPISignatures');

                    return (
                        <SignatureStatus signatureExpectations={piSignatureExpectations} />
                    );
                },
            },
            {
                title: 'Staff Signature',
                field: 'pendingStaffSignatures',
                hidden: lodash.get(props, 'delegationMode') === 'CENTRAL',
                headerStyle: {
                    width: '16.7%',
                    minWidth: '16.7%',
                },
                cellStyle: {
                    width: '16.7%',
                    minWidth: '16.7%',
                },
                render: (data) => {
                    const staffSignatureExpectations = lodash.get(data, 'pendingStaffSignatures');

                    return (
                        <SignatureStatus signatureExpectations={staffSignatureExpectations} />
                    );
                },
            },
            {
                title: '',
                field: 'openDelegation',
                hidden: false,
                filtering: false,
                sorting: false,
                cellStyle: {
                    width: '10%',
                    minWidth: '10%',
                    textAlign: 'right',
                    textDecoration: 'none',
                },
                headerStyle: {
                    width: '10%',
                    minWidth: '10%',
                },
                render: () => (
                    <Button
                        type={BUTTON.SIZE.SECONDARY}
                        color={BUTTON.COLOR.BLUE}
                        bgcolor={BUTTON.BGCOLOR.CLEAR}
                        onClick={onRowClick}
                    >
                        Open Delegation
                    </Button>
                ),
            },
        ];

        const conflictColumn = {
            hidden: false,
            render: (rowData) => {
                const { studyTeam } = props;
                const delegationExpired = moment().isAfter(rowData.rawEndDate);

                if (delegationExpired) {
                    return null;
                }

                const foundTeam = studyTeam.find(team => team.user.id === rowData.userId);

                return (
                    <Conditional condition={foundTeam && foundTeam.studyRoleId !== rowData.delegationRoleId}>
                        <ConflictRoleTooltip title='Check your Study Team list to ensure this user’s role is in sync with this DOA update.' placement="bottom-start" interactive>
                            <span className={`${icons.materialIcons} ${styles.studyDOAConflictRoleError}`}>error_outline</span>
                        </ConflictRoleTooltip>
                    </Conditional>
                );
            },
        };

        const { studyTeam, delegations, pathname } = props;
        const isInStudyDOAContext = pathname.startsWith('/study/');
        if (isInStudyDOAContext && studyTeam.length !== 0 && delegations.length !== 0) {
            let foundConflictRole = delegations.find(delegation => studyTeam.some(team =>
                team.user.id === delegation.user.id && team.studyRoleId !== delegation.role.id));
            if (foundConflictRole) {
                delegationColumns.unshift(conflictColumn);
            }
        }

        return delegationColumns;
    };

    const onRowClick = (event, data) => {
        // Handle side panel toggle
        props.onRowClick(data);
    };

    if (!props.studyId) {
        return (
            <ThemeProvider theme={theme}>
                <DefaultMaterialTable
                    className={styles.DelegationTable}
                    options={optionsBuilder({
                        filtering: false,
                        search: false,
                        headerStyle: {
                            fontFamily: 'Hind',
                            fontWeight: 500,
                            fontSize: '1rem',
                            whiteSpace: 'nowrap',
                        },
                        rowStyle: { fontSize: '0.9rem' },
                        paging: lodash.get(rows, 'length') > 0,
                    })}
                    columns={columns}
                    data={rows}
                    onRowClick={onRowClick}
                    components={{
                        Toolbar: (props) => null,
                    }}
                />
            </ThemeProvider>
        );
    }
    else {
        return (
            <ThemeProvider theme={themeStudy}>
                <MaterialTable
                    className={styles.DelegationTable}
                    icons={tableIcons}
                    localization={localization}
                    options={optionsBuilder({
                        filtering: false,
                        search: false,
                        headerStyle: {
                            fontFamily: 'Hind',
                            fontWeight: 500,
                            fontSize: '1rem',
                            whiteSpace: 'nowrap',
                            padding: '0px 10px 0px 0px',
                        },
                        rowStyle: { fontSize: '0.9rem' },
                        paging: lodash.get(rows, 'length') > 0,
                    })}
                    columns={columns}
                    data={rows}
                    onRowClick={onRowClick}
                    components={{
                        Toolbar: (props) => null,
                    }}
                />
            </ThemeProvider>
        );
    }
}

export default DelegationTable;